import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close,
  CompareArrows,
  Edit as EditIcon,
  ImageNotSupported,
} from "@mui/icons-material";
import { ReactNode, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useBlobUrl } from "./useBlobUrl";
import { EditEntityDialog, EditTarget } from "./EditEntityDialog";

/**
 * DocumentCompareDrawer — canonical "View documents" side-by-side experience.
 *
 * Extracted from MatchReview so it can be reused across every list page that
 * wants "click a row → see the two relevant files next to each other". It has
 * two modes:
 *
 * 1. Compare mode (default):
 *    Pass `left` and `right` panel configs, each with a storageId + label.
 *    The drawer renders both panels side-by-side at the bottom of the screen,
 *    each supporting image + PDF previews. A missing storageId shows a "No
 *    document" placeholder instead of an error.
 *
 * 2. Details-only mode:
 *    Pass `detailsContent` instead of (or in addition to) the panels. Used
 *    by AuditTrail / PipelineErrors when the row points at an entity that
 *    isn't a transaction or invoice (e.g. rule / system / cycle) — there are
 *    no files to show, so we reuse the same drawer chrome but swap the panel
 *    area for a full-width details block (typically formatted JSON).
 */

export interface DocPanelConfig {
  /** Convex `_storage` id. Pass `null` to show the "no document" placeholder. */
  storageId: string | null;
  /** Uppercase label above the panel, e.g. "BANK STATEMENT" or "INVOICE". */
  label: string;
}

export interface DocumentCompareDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Bold header line (e.g. "Document Comparison" or "Audit Entry"). */
  title: string;
  /** Smaller secondary line (e.g. vendor + amount delta, or timestamp + agent). */
  subtitle?: string;
  /** Left panel config. Omit to enter details-only mode. */
  left?: DocPanelConfig;
  /** Right panel config. Omit to enter details-only mode. */
  right?: DocPanelConfig;
  /**
   * Optional details block rendered below (or instead of) the file panels.
   * When `left`/`right` are omitted the drawer renders this alone, full-width.
   */
  detailsContent?: ReactNode;
  /**
   * When provided, renders an "Edit" button in the drawer header that opens
   * the shared EditEntityDialog. Pass `{ type: "transaction", id }` for
   * transactions and `{ type: "invoice", id }` for invoices. The dialog
   * forces invoice edits to confidence=100 / extractionStatus=extracted so
   * the matching pipeline picks the row up again.
   */
  editTarget?: EditTarget | null;
  /** Optional action rendered above the left (Bank Statement) panel, e.g. a statement-swap control. */
  leftAction?: React.ReactNode;
}

export function DocumentCompareDrawer({
  open,
  onClose,
  title,
  subtitle,
  left,
  right,
  detailsContent,
  editTarget,
  leftAction,
}: DocumentCompareDrawerProps) {
  const hasPanels = Boolean(left || right);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "85vh",
          bgcolor: "var(--c-bg-elevated)",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2, borderBottom: "1px solid var(--c-overlay-6)" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1, mr: 2 }}>
          <CompareArrows sx={{ color: "var(--c-link-accent)" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {editTarget && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Edit{" "}
              {editTarget.type === "transaction" ? "transaction" : "invoice"}
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </Stack>

      {hasPanels ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ flex: 1, p: 2, overflow: "hidden", minHeight: 0 }}
        >
          {left && (
            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
              {leftAction}
              <DocPanel config={left} open={open} />
            </Stack>
          )}
          {right && <DocPanel config={right} open={open} />}
        </Stack>
      ) : null}

      {detailsContent && (
        <Box
          sx={{
            flex: hasPanels ? "0 0 auto" : 1,
            px: 2,
            pb: 2,
            pt: hasPanels ? 0 : 2,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {detailsContent}
        </Box>
      )}

      {editTarget && (
        <EditEntityDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          target={editTarget}
        />
      )}
    </Drawer>
  );
}

/**
 * Single-file preview panel used inside the compare drawer.
 *
 * Resolves the storage URL via `api.storage.getUrl`, fetches the bytes through
 * the nginx /convex proxy (via useBlobUrl), then renders an <img> or <iframe>
 * depending on the content type. Unknown content types fall back to a download
 * link.
 */
function DocPanel({ config, open }: { config: DocPanelConfig; open: boolean }) {
  const { storageId, label } = config;
  const rawUrl = useQuery(
    api.storage.getUrl,
    storageId && open ? { storageId: storageId as any } : "skip",
  );
  const metadata = useQuery(
    api.storage.getMetadata,
    storageId && open ? { storageId: storageId as any } : "skip",
  );

  const { blobUrl, loading, error } = useBlobUrl(rawUrl ?? null);
  const contentType = metadata?.contentType ?? "";
  const isImage = contentType.startsWith("image/");
  const isPdf = contentType === "application/pdf";

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          fontSize: "0.7rem",
          letterSpacing: "0.08em",
          mb: 1,
          px: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          border: "1px solid var(--c-overlay-8)",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "var(--c-bg-elevated)",
          minHeight: 0,
        }}
      >
        {!storageId ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              minHeight: 300,
              flexDirection: "column",
            }}
          >
            <ImageNotSupported sx={{ fontSize: 40, color: "var(--c-text-muted)", mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              No document
            </Typography>
          </Box>
        ) : rawUrl === undefined || loading ? (
          <Box sx={{ p: 4 }}>
            <LinearProgress />
          </Box>
        ) : error || !blobUrl ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              minHeight: 300,
              flexDirection: "column",
            }}
          >
            <ImageNotSupported sx={{ fontSize: 40, color: "var(--c-text-muted)", mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Could not load file
            </Typography>
          </Box>
        ) : isImage ? (
          <Box
            component="img"
            src={blobUrl}
            alt={label}
            sx={{ width: "100%", display: "block" }}
          />
        ) : isPdf ? (
          <Box
            component="iframe"
            src={blobUrl}
            sx={{
              width: "100%",
              height: "100%",
              border: "none",
              bgcolor: "#fff",
            }}
            title={label}
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Unsupported format: {contentType}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--c-link-accent)" }}
              >
                Download file
              </a>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Helper to render a pre-formatted JSON block for the `detailsContent` slot.
 * Matches the style used by PipelineErrors' existing expandable row.
 */
export function DetailsJsonBlock({ data }: { data: unknown }) {
  let text: string;
  if (typeof data === "string") {
    try {
      text = JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      text = data;
    }
  } else {
    text = JSON.stringify(data, null, 2);
  }
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: "rgba(0,0,0,0.3)",
        fontFamily: "monospace",
        fontSize: "0.75rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        maxHeight: "100%",
        overflow: "auto",
      }}
    >
      {text}
    </Box>
  );
}
